import { createContext, useContext, useMemo, cloneElement } from "react";

import { Flex, FlexProps } from "@chakra-ui/react";

import { Badge } from "ui/Badge/Badge";
import { TitleXS } from "ui/Typography/Typography";

export type PillProps = {
  children: React.ReactNode;
  color?: string;
  bg?: string;
  bordered?: boolean;
} & FlexProps;

const PillContext = createContext<{ color: string }>({ color: "transparent" });

export function Pill({
  children,
  color = "transparent",
  bg = "transparent",
  bordered,
  ...rest
}: PillProps) {
  const value = useMemo(() => ({ color }), [color]);
  return (
    <PillContext.Provider value={value}>
      <Flex
        bg={bg}
        borderRadius="full"
        borderColor={bordered ? color : "transparent"}
        borderWidth="1px"
        alignItems="center"
        gap="s50"
        w="fit-content"
        px="s100"
        py="s25"
        {...rest}
      >
        {children}
      </Flex>
    </PillContext.Provider>
  );
}

function PillText({ children }: { children: React.ReactNode }) {
  return <TitleXS>{children}</TitleXS>;
}

function PillIcon({ icon }: { icon: React.ReactElement<any> }) {
  const { color } = useContext(PillContext);
  return <>{cloneElement(icon, { color })}</>;
}

function PillNumber({ children }: { children: React.ReactNode }) {
  const { color } = useContext(PillContext);
  return <TitleXS color={color}>{children}</TitleXS>;
}

function PillBadge({ color }: { color: string }) {
  return <Badge size="small" color={color} />;
}

Pill.Text = PillText;
Pill.Icon = PillIcon;
Pill.Number = PillNumber;
Pill.Badge = PillBadge;
